import {
  ApplicationConfig,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';

import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environment/environment';
import { TenantHeaderInterceptor } from './_interceptors/tenant-header.interceptor';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { MarkdownModule, MARKED_OPTIONS, MarkedRenderer } from 'ngx-markdown';
import { Token } from 'marked';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';

import {
  MsalService,
  MsalGuard,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
  MsalInterceptor,
  MsalBroadcastService
} from '@azure/msal-angular';


export function markedOptionsFactory() {
  const renderer = new MarkedRenderer();

  // open links in a new tab
  renderer.link = (link: {
    href: string;
    title?: string | null;
    text: string;
    tokens: Token[];
  }) => {
    const { href, title, text } = link;
    const titleAttr = title ? ` title="${title}"` : '';
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener">${text}</a>`;
  };

  return {
    renderer: renderer,
    gfm: true,
    breaks: true,
    tables: true,
    smartLists: true,
    smartypants: true,
  };
}

function msalProviders(): (Provider | EnvironmentProviders)[] {
  const scopes = [`api://${environment.msal.clientId}/User.Read`];

  return [
    {
      provide: MSAL_INSTANCE,
      useValue: new PublicClientApplication({
        auth: {
          clientId: environment.msal.clientId,
          authority: `https://login.microsoftonline.com/${environment.msal.tenantId}`,
          redirectUri: window.origin
        },
        cache: {
          cacheLocation: 'localStorage',
        }
      })
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: scopes
        },
      } as MsalGuardConfiguration
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useValue: {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: scopes
        },
        protectedResourceMap: new Map([
          [environment.apiUrl + '/*', scopes]
        ])
      } as MsalInterceptorConfiguration
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAppInitializer(() => {
      const msalService = inject(MsalService);
      return msalService.initialize();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
  ]
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: 'env', useValue: environment },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantHeaderInterceptor,
      multi: true,
    },
    provideTranslateService({
      lang: 'nl-NL',
      fallbackLang: 'nl-NL',
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        suffix: '.json',
      }),
    }),
    ...msalProviders(),
    importProvidersFrom(
      MarkdownModule.forRoot({
        markedOptions: {
          provide: MARKED_OPTIONS,
          useFactory: markedOptionsFactory,
        },
      }),
    ),
  ],
};

